import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateAdminAccountFilterQuery,
	useAdminAccountFilterQuery,
} from '../../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { invalidateAdminAccountSnapshotByMonthQuery } from '../../../../queries/Admin/AccountSnapshot/useAdminAccountSnapshotByMonthQuery';
import { AccountAdminViewModel, IAccountFilterCriteria } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { ComputerCoffeeGraphic } from '../../../../web/components/svgs/graphics/ComputerCoffeeGraphic';
import { bs } from '../../../../web/styles/styles';
import { RenewalSummaryHeader } from '../../../components/reporting/RenewalSummaryHeader';
import { ReportRowAccount } from '../../../components/reporting/ReportRowAccount';
import { styleSheet } from './styles';

interface Props {
	filter?: IAccountFilterCriteria;
	unassignedCsmFilter?: boolean;
}

export const invalidateMonthlyRenewalQueries = () => {
	invalidateAdminAccountFilterQuery();
	invalidateAdminAccountSnapshotByMonthQuery();
};

function _MonthlyRenewalAccounts({ filter, unassignedCsmFilter }: Props) {
	const userSession = useUserSession();
	const adminAccountFilterQuery = useAdminAccountFilterQuery({
		filter,
		sort: 'asc',
		sortBy: 'planDetails.renewalDate',
	});
	const deletedAccountFilterQuery = useAdminAccountFilterQuery({
		deleted: true,
		filter,
		sort: 'asc',
		sortBy: 'planDetails.renewalDate',
	});
	// Function to check if an account has an unassigned CSM
	const hasUnassignedCsm = (account: AccountAdminViewModel) => {
		const accountData = account.toJs();
		const customerSuccessName = accountData?.additionalInfo?.customerSuccessName;
		return !customerSuccessName || customerSuccessName === '';
	};

	const accounts =
		adminAccountFilterQuery?.data?.pages?.flatMap(page =>
			page.values.map(value => new AccountAdminViewModel(userSession, value))
		) ?? [];
	const deletedAccounts =
		deletedAccountFilterQuery?.data?.pages?.flatMap(page =>
			page.values.map(value => new AccountAdminViewModel(userSession, value))
		) ?? [];
	const isLoading = adminAccountFilterQuery.isPending || deletedAccountFilterQuery.isPending;

	const filteredAccounts = unassignedCsmFilter ? accounts.filter(hasUnassignedCsm) : accounts;

	return (
		<div className={css(styleSheet.container)}>
			<RenewalSummaryHeader />
			<div className={css(styleSheet.rows)} data-scroll-restoration-id='admin-monthly-renewal-report-accounts-table'>
				{filteredAccounts.map(account => {
					return <ReportRowAccount account={account} key={account.id} />;
				})}
				{!adminAccountFilterQuery.isPending && adminAccountFilterQuery.hasNextPage ? (
					<Waypoint
						bottomOffset='-200px'
						onEnter={() => {
							adminAccountFilterQuery.fetchNextPage();
						}}
					/>
				) : null}
				{isLoading && (
					<div className={css(styleSheet.spinnerContainer)}>
						<LoadingSpinner type='large' />
					</div>
				)}
				<div className={css(styleSheet.deletedAccounts)}>deleted accounts</div>
				{deletedAccounts?.length > 0 &&
					deletedAccounts.map(account => {
						return <ReportRowAccount account={account} key={account.id} disabled={true} />;
					})}{' '}
				{adminAccountFilterQuery.isSuccess &&
					filteredAccounts.length === 0 &&
					deletedAccountFilterQuery.isSuccess &&
					deletedAccounts.length === 0 && (
						<>
							<div className={css(bs.flexCenter)}>
								<ComputerCoffeeGraphic />
							</div>
							<div className={css(styleSheet.noneFound)}>No available accounts for renewals this month.</div>
						</>
					)}
			</div>
		</div>
	);
}

export const MonthlyRenewalAccounts = observer(_MonthlyRenewalAccounts);
